import type { BranchAction, FlowContext, FlowStep, OrderSnapshot } from "./types";
import {
  assignRiderStep,
  cancelEnquiryStep,
  cancelOrderStep,
  ackEnquiryStep,
  createEnquiryStep,
  createShipmentStep,
  customerHoldStep,
  customerUnholdStep,
  deliveredEventStep,
  draftEstimateSharedStep,
  enquiryCloseStep,
  estimateConfigureStep,
  estimateConfirmStep,
  estimateCreateStep,
  estimateShareStep,
  draftEstimateRespondedStep,
  invoiceEventStep,
  markDeliveredStep,
  partConfirmationRequestStep,
  partConfirmationRespondStep,
  partConversionEndStep,
  partConversionStartStep,
  readyToDispatchStep,
  supplierAckStep,
  supplierCancelStep,
  supplierCboStep,
  supplierInvoiceMediaStep,
  supplierReadyStep,
  supplierReviewStep,
  supplierSessionStep,
} from "./steps";

export interface FlowPhase {
  id: string;
  label: string;
  stepIds: string[];
}

/** Ordered happy path: Enquiry → … → Delivered. */
export const HAPPY_PATH: FlowStep[] = [
  createEnquiryStep,
  ackEnquiryStep,
  partConversionStartStep,
  partConversionEndStep,
  draftEstimateSharedStep,
  draftEstimateRespondedStep,
  partConfirmationRequestStep,
  partConfirmationRespondStep,
  estimateCreateStep,
  estimateConfigureStep,
  estimateShareStep,
  estimateConfirmStep,
  supplierSessionStep,
  supplierAckStep,
  supplierInvoiceMediaStep,
  supplierReviewStep,
  supplierReadyStep,
  invoiceEventStep,
  readyToDispatchStep,
  assignRiderStep,
  createShipmentStep,
  markDeliveredStep,
  deliveredEventStep,
];

export const BRANCH_STEPS: FlowStep[] = [
  cancelEnquiryStep,
  enquiryCloseStep,
  cancelOrderStep,
  supplierCancelStep,
  supplierCboStep,
  customerHoldStep,
  customerUnholdStep,
];

export const ALL_STEPS: FlowStep[] = [...HAPPY_PATH, ...BRANCH_STEPS];

export const PHASES: FlowPhase[] = [
  {
    id: "enquiry",
    label: "Enquiry",
    stepIds: [
      createEnquiryStep.id,
      ackEnquiryStep.id,
      partConversionStartStep.id,
      partConversionEndStep.id,
      draftEstimateSharedStep.id,
      draftEstimateRespondedStep.id,
    ],
  },
  {
    id: "confirmation",
    label: "Part confirmation",
    stepIds: [partConfirmationRequestStep.id, partConfirmationRespondStep.id],
  },
  {
    id: "estimate",
    label: "Estimate",
    stepIds: [
      estimateCreateStep.id,
      estimateConfigureStep.id,
      estimateShareStep.id,
      estimateConfirmStep.id,
    ],
  },
  {
    id: "supplier",
    label: "Supplier fulfilment",
    stepIds: [
      supplierSessionStep.id,
      supplierAckStep.id,
      supplierInvoiceMediaStep.id,
      supplierReviewStep.id,
      supplierReadyStep.id,
    ],
  },
  {
    id: "dispatch",
    label: "Dispatch & delivery",
    stepIds: [
      invoiceEventStep.id,
      readyToDispatchStep.id,
      assignRiderStep.id,
      createShipmentStep.id,
      markDeliveredStep.id,
      deliveredEventStep.id,
    ],
  },
];

export const stepById = (id: string): FlowStep | undefined =>
  ALL_STEPS.find((s) => s.id === id);

export const stepsUntil = (targetStepId: string): FlowStep[] => {
  const idx = HAPPY_PATH.findIndex((s) => s.id === targetStepId);
  return idx === -1 ? HAPPY_PATH : HAPPY_PATH.slice(0, idx + 1);
};

export const phaseOfStep = (stepId: string) =>
  PHASES.find((p) => p.stepIds.includes(stepId));

/** Selectable stopping points (event-only steps are still valid targets). */
export const TARGETS = HAPPY_PATH.map((s) => ({
  id: s.id,
  label: s.targetLabel,
  phase: phaseOfStep(s.id)?.label || "",
  optional: Boolean(s.optional),
}));

export const DEFAULT_TARGET_ID = deliveredEventStep.id;

// PART_CONVERSION_COMPLETED and the estimate states mirror ACK's exits;
// PART_CONVERSION_STARTED only completes.
const ENQUIRY_OPEN = [
  "ACK",
  "PART_CONVERSION_COMPLETED",
  "DRAFT_ESTIMATE_SHARED",
  "DRAFT_ESTIMATE_RESPONDED",
];
const GARAAZ_CANCELLABLE = ["NEW", ...ENQUIRY_OPEN, "BLOCKED", "ALLOCATED", "CBO"];
const GARAAZ_HOLDABLE = ["NEW", ...ENQUIRY_OPEN, "ALLOCATED"];
const ENQUIRY_CLOSABLE = ENQUIRY_OPEN;
const SUPPLIER_CANCELLABLE = ["NEW", "ACK", "PICKED", "IN_REVIEW", "CBO"];
const SUPPLIER_CBO_FROM = ["ACK", "PICKED", "IN_REVIEW"];

/**
 * Mirrors the backend XState guards closely enough to only expose actions that
 * the current order/supplier state accepts. The backend remains the authority;
 * a rejected action still surfaces as a failed step with the real error.
 */
export const getValidActions = (
  snapshot: OrderSnapshot | undefined,
  ctx: Pick<FlowContext, "child" | "enquiry"> | undefined
): BranchAction[] => {
  if (!snapshot?.enquiry || !ctx?.enquiry) return [];
  const actions: BranchAction[] = [];
  const enquiryStatus = snapshot.enquiry.orderStatus;
  const child = snapshot.child;

  if (!child) {
    if (GARAAZ_CANCELLABLE.includes(enquiryStatus)) {
      actions.push({
        id: cancelEnquiryStep.id,
        stepId: cancelEnquiryStep.id,
        label: "Cancel enquiry",
        description: `Enquiry is ${enquiryStatus}; CANCEL is accepted by the Garaaz machine.`,
        tone: "danger",
      });
    }
    if (ENQUIRY_CLOSABLE.includes(enquiryStatus)) {
      actions.push({
        id: enquiryCloseStep.id,
        stepId: enquiryCloseStep.id,
        label: "Close enquiry",
        description:
          "ENQUIRY_CLOSED is valid from ACK, PART_CONVERSION_COMPLETED, DRAFT_ESTIMATE_SHARED and DRAFT_ESTIMATE_RESPONDED.",
        tone: "neutral",
      });
    }
    if (GARAAZ_HOLDABLE.includes(enquiryStatus)) {
      actions.push({
        id: customerHoldStep.id,
        stepId: customerHoldStep.id,
        label: "Customer hold (enquiry)",
        description: "Pause the enquiry on customer request.",
        tone: "neutral",
      });
    }
    if (enquiryStatus === "CUSTOMER_HOLD") {
      actions.push({
        id: customerUnholdStep.id,
        stepId: customerUnholdStep.id,
        label: "Resume enquiry",
        description: "CUSTOMER_UNHOLD returns the enquiry to ACK (or its part conversion state).",
        tone: "primary",
      });
    }
    return actions;
  }

  const supplierReady = child.supplierStatus === "READY";
  if (GARAAZ_CANCELLABLE.includes(child.orderStatus) && !(child.orderStatus === "BLOCKED" && supplierReady)) {
    actions.push({
      id: cancelOrderStep.id,
      stepId: cancelOrderStep.id,
      label: "Cancel order",
      description: `Order is ${child.orderStatus}, supplier ${child.supplierStatus}; OrCA CANCEL is accepted.`,
      tone: "danger",
    });
  }
  if (GARAAZ_HOLDABLE.includes(child.orderStatus)) {
    actions.push({
      id: customerHoldStep.id,
      stepId: customerHoldStep.id,
      label: "Customer hold",
      description: "Pause the order on customer request.",
      tone: "neutral",
    });
  }
  if (child.orderStatus === "CUSTOMER_HOLD") {
    actions.push({
      id: customerUnholdStep.id,
      stepId: customerUnholdStep.id,
      label: "Resume order",
      description: "CUSTOMER_UNHOLD resumes processing.",
      tone: "primary",
    });
  }
  if (child.supplierStatus && SUPPLIER_CANCELLABLE.includes(child.supplierStatus)) {
    actions.push({
      id: supplierCancelStep.id,
      stepId: supplierCancelStep.id,
      label: "Supplier cancels",
      description: `Supplier allocation is ${child.supplierStatus}; supplier CANCEL is accepted.`,
      tone: "danger",
    });
  }
  if (child.supplierStatus && SUPPLIER_CBO_FROM.includes(child.supplierStatus)) {
    actions.push({
      id: supplierCboStep.id,
      stepId: supplierCboStep.id,
      label: "Supplier CBO",
      description: "Supplier marks the allocation as back-ordered.",
      tone: "neutral",
    });
  }
  return actions;
};
